import { useId, useState, type FormEvent } from 'react'
import { BellRing, CheckCircle2 } from 'lucide-react'
import { race } from '../../shared/race.config'
import { issuesToErrors, notifySchema } from '../../shared/schemas'
import { api, ApiError } from '../lib/api'
import { TextField } from './form'
import { Button, Notice } from './ui'

/**
 * "Email me when registration opens": an email address and, optionally, a first name.
 * `compact` puts it on one row for cards; the full version suits the Register page.
 */
export function NotifyForm({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  const uid = useId()
  const [form, setForm] = useState({ email: '', firstName: '', website: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const res = notifySchema.safeParse(form)
    if (!res.success) {
      setErrors(issuesToErrors(res.error))
      return
    }
    setErrors({})
    setBusy(true)
    setFailure(null)
    try {
      await api.notify(form)
      setDone(res.data.email)
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setErrors(err.fields)
      else setFailure(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className={`flex items-start gap-3 rounded-xl bg-brand-soft p-4 ${className}`} role="status">
        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
        <div>
          <p className="font-semibold">You’re on the list</p>
          <p className="text-[0.95rem] text-ink-2">
            We’ll email <span className="font-semibold text-ink">{done}</span> as soon as registration opens. Check your inbox for a confirmation.
          </p>
        </div>
      </div>
    )

  return (
    <form onSubmit={submit} noValidate className={`flex flex-col gap-3 ${className}`} aria-label="Get notified when registration opens">
      <div className={compact ? 'grid gap-3 sm:grid-cols-[1.4fr_1fr_auto] sm:items-end' : 'grid gap-4 sm:grid-cols-2'}>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          fieldId={`${uid}-email`}
          value={form.email}
          error={errors.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <TextField
          label="First name"
          optional
          autoComplete="given-name"
          fieldId={`${uid}-first`}
          value={form.firstName}
          error={errors.firstName}
          onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
        />
        {/* Honeypot: hidden from people and screen readers; bots tend to fill it in. */}
        <div className="hidden" aria-hidden>
          <label>
            Website
            <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
          </label>
        </div>
        {compact && (
          <Button type="submit" busy={busy} className="sm:mb-0.5">
            <BellRing className="size-4" aria-hidden /> Notify me
          </Button>
        )}
      </div>
      {!compact && (
        <div>
          <Button type="submit" size="lg" busy={busy}>
            <BellRing className="size-5" aria-hidden /> Notify me when registration opens
          </Button>
        </div>
      )}
      {failure && <Notice tone="danger">{failure}</Notice>}
      <p className="text-sm text-ink-3">
        We’ll only use your email to tell you about {race.name} registration, and every email has an unsubscribe link.
      </p>
    </form>
  )
}
